import { useEffect, useState, useContext } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import LoadingScreen from "@/components/LoadingScreen";

import { UserContext } from "@/context/UserContext";
import { useEmployees } from "@/context/EmployeesContext";
import {
  getAdministrationFallbackPath,
  getCurrentLocationPath,
  getLoginPathWithRedirect,
  isAdministrator,
} from "@/utils/authRedirect";

const RequireAuth = ({ children, requireAdmin = false }) => {
  const [loading, setLoading] = useState(true);
  const { user, setUser } = useContext(UserContext);
  const { loadEmployees, clearEmployees } = useEmployees();

  const navigate = useNavigate();
  const location = useLocation();
  const loginPath = getLoginPathWithRedirect(location);

  // Функция для сравнения пользователей по employee_uuid
  const isUserChanged = (newUser, currentUser) => {
    if (!newUser && !currentUser) return false;
    if (!newUser || !currentUser) return true;

    // Сравниваем только по employee_uuid
    return newUser.employee_uuid !== currentUser.employee_uuid;
  };

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch("/api/me");
        const data = await response.json();

        if (data.user) {
          // Устанавливаем пользователя только если его нет или если employee_uuid изменился
          if (!user || isUserChanged(data.user, user)) {
            setUser(data.user);
          }
          // Если пользователь уже установлен и employee_uuid тот же - ничего не делаем
        } else {
          // Очищаем пользователя только если он был установлен
          if (user) {
            setUser(null);
            clearEmployees();
            navigate(loginPath, {
              state: { from: getCurrentLocationPath(location) },
              replace: true,
            });
          }
        }
      } catch (error) {
        console.error("Ошибка проверки авторизации:", error);
        if (user) {
          setUser(null);
          clearEmployees();
          navigate(loginPath, {
            state: { from: getCurrentLocationPath(location) },
            replace: true,
          });
        }
      } finally {
        if (loading) {
          setLoading(false);
        }
      }
    };

    checkAuth();

    // Устанавливаем интервал для проверки авторизации каждые 10 секунд
    const authInterval = setInterval(checkAuth, 10000);

    return () => {
      clearInterval(authInterval);
    };
  }, [
    user,
    loading,
    location,
    loginPath,
    navigate,
    setUser,
    clearEmployees,
  ]);

  // Загружаем сотрудников когда пользователь становится авторизованным
  useEffect(() => {
    if (user && !loading) {
      loadEmployees();
    }
    // eslint-disable-next-line
  }, [user, loading]);

  if (loading) {
    return <LoadingScreen />;
  }

  if (user && requireAdmin && !isAdministrator(user)) {
    return <Navigate to={getAdministrationFallbackPath(location)} replace />;
  }

  return user ? (
    children
  ) : (
    <Navigate
      to={loginPath}
      state={{ from: getCurrentLocationPath(location) }}
      replace
    />
  );
};

export default RequireAuth;
